import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dropdown from '../../../../common/_custom/Dropdown';
import { DropdownOption } from '../../../../common/_custom/Dropdown/dropdownTypes';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import { fetchBankStatementData } from '../../../../store/dashboard/bankStatement/apiHelper';
import { BSA_REPORT_VIEWER_HEADING } from '../../../../store/dashboard/bankStatement/constants';
import { useBsaReport } from './useBsaReport';
import styles from './BankDataTable.module.scss';
import EmptyBank from './components/EmptyBank/EmptyBank';
import Header from './components/Header';
import Rows from './components/Rows';
import BankingOverview from './components/BankingOverview/BankingOverview';
import FinancialsFromBankingV2 from '../Financials/components/FinancialsFromBankingV2/FinancialsFromBankingV2';
import { CASHFLOW_FROM_BANKING } from '../Financials/constants';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { BackButtonLeftFill, changeCircleBlue } from '../../../../assets/hostedassets';
import CtaPill from '../../../../common/_custom/CtaPill/CtaPill';
import { EXCEL_ANALYSIS_LABEL, EXCEL_ANALYSIS_LOADING_LABEL } from '../../../../common/_custom/CtaPill/ctaPillConstants';
import FileViewerDrawer from '../../../../common/DocumentViewer/FileViewerDrawer';
import { updateQueryParams } from '../../../../utils/searchParamUtils';
import { AGGREGATED } from '../../../../utils/constants/commonConstants';
import { isEmpty } from 'lodash';
import { useAppSelector } from '../../../../app/hooks';
import { useExtractBankStatements } from '../../../../store/banking/useExtractBankStatements';
import {
    selectBankingPendingCount,
    selectBankingInFlightCount,
    selectBankingExtractedCount,
    selectBankingTriggering,
} from '../../../../store/banking/selectors';
import { CLICK_SOURCE } from '../../../../store/banking/constants';
import type { ClickSource } from '../../../../store/banking/constants';
import { useVisibilityConfig } from '../../../../utils/useVisibilityConfig';
import { VISIBILITY_KEYS } from '../../../../utils/constants/visibilityKeys';
import { useBoundedClassificationPoll } from '../../../../common/hooks/useBoundedClassificationPoll';
import { useLatestRequestGuard } from '../../../../common/hooks/useLatestRequestGuard';
import { BankDataEnvelope, deriveBankDataEnvelope, useBankDateRange } from './useBankDateRange';
import TimePeriodFilter from './components/TimePeriodFilter/TimePeriodFilter';
import ThreeDotMenu from '../../../../common/ThreeDotMenu/ThreeDotMenu';
import TypeSelector from '../../components/TypeSelector/TypeSelector';
import { monthKeyToDisplay } from '../../../../utils/dateUtils';
import EditableDropdown from '../../../../common/EditableDropdown/EditableDropdown';
import { useUnitPreference } from '../AuditedFinancialsVisualisation/useUnitPreference';
import { AUDITED_UNIT_OPTIONS, AUDITED_UNIT_DIVISOR, AUDITED_UNIT_DEFAULT } from '../AuditedFinancialsVisualisation/auditedStatementLayout';
import {
    CFB_UNIT_STORAGE_KEY,
    BANK_STATEMENTS,
    CFB_INLINE_RANGES_MIN_WIDTH,
    BANK_STATEMENTS_HEADER_MIN_WIDTH,
    BANK_STATEMENTS_HEADER_MIN_WIDTH_WITH_ACCOUNT,
    CFB_QUICK_RANGES,
    BANKING_TYPES,
    QUICK_RANGE_KEYS,
} from './constants';

type BankTableData = {
    isFetched: boolean;
    accounts?: string;
    summaryData?: SingleAccountData[];
    bankingOverview?: BankingOverviewData[];
    classificationPending?: boolean;
};

type BankingOverviewData = {
    accountId: string;
    name: string;
    accountNumber: string;
    closingBalance: number;
    closingBalanceDate: string | null;
    odSanctionLimit: number;
    transactionsLTM: number;
    missingDays: number;
};

type SingleAccountData = {
    accountNumber: string;
    bankName: string;
    headers: any[];
    rows: any[];
};

interface BankStatementsBreadcrumbProps {
    selectedAccountId: string | null;
    accountLabel?: string;
    onBack: () => void;
}

function BankStatementsBreadcrumb({ selectedAccountId, accountLabel, onBack }: BankStatementsBreadcrumbProps) {
    return (
        <div
            style={
                selectedAccountId
                    ? { display: 'flex', alignItems: 'center', marginBottom: '1rem', height: '4rem' }
                    : { visibility: 'hidden', marginBottom: '0rem', height: '0rem', display: 'flex', alignItems: 'center' }
            }
        >
            {selectedAccountId && (
                <>
                    <button onClick={onBack} className={styles.BackButton}>
                        <img src={BackButtonLeftFill} alt="Back" style={{ height: '14px', width: '14px' }} />
                        <span style={{ fontFamily: 'Gilroy-Bold,sans-serif', fontSize: '1rem' }}>Banking Overview</span>
                    </button>
                    <div> {'>'}</div>
                    <div className={styles.AccountName}>{accountLabel}</div>
                </>
            )}
        </div>
    );
}

interface BankDataTableProps {
    setDashboardTab?: (tab: { selectedTab: string; tabType: string }) => void;
}

function BankDataTable({ setDashboardTab }: BankDataTableProps) {
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedBankingType, setSelectedBankingType] = useState(
        searchParams.get('bankingType') === CASHFLOW_FROM_BANKING ? CASHFLOW_FROM_BANKING : BANK_STATEMENTS,
    );
    // The old financialsType=cashflowFromBanking deep link is rewritten to bankingType by the
    // Dashboard AFTER first mount — track the param so the redirect actually lands on the pill.
    const bankingTypeParam = searchParams.get('bankingType');
    useEffect(() => {
        const next = bankingTypeParam === CASHFLOW_FROM_BANKING ? CASHFLOW_FROM_BANKING : BANK_STATEMENTS;
        setSelectedBankingType((prev) => (prev === next ? prev : next));
    }, [bankingTypeParam]);
    const [tableData, setTableData] = useState<BankTableData>({
        isFetched: false,
    });
    const params = useParams();
    const [selectedAccount, setSelectedAccount] = useState<null | DropdownOption>(null);
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
    const [loadingData, setLoadingData] = useState(false);
    const {
        trigger: triggerBsaReport,
        loading: bsaReportLoading,
        showFile: showBsaFile,
        fileUrl: bsaFileUrl,
        fileMimeType: bsaFileMimeType,
        fileName: bsaFileName,
        closeFile: closeBsaFile,
        onDownload: downloadBsaFile,
    } = useBsaReport(params.id as string);
    const tableRef = useRef<HTMLDivElement>(null);
    const location = useLocation();

    // Measured-row refs: collapse the CFB quick ranges / Bank Statements Excel Analysis
    // action into a ⋮ menu once their row narrows below its threshold.
    const cfbToolbarRef = useRef<HTMLDivElement>(null);
    const [collapseQuickRanges, setCollapseQuickRanges] = useState(false);
    const bankStatementsHeaderRef = useRef<HTMLDivElement>(null);
    const [collapseExcelAnalysis, setCollapseExcelAnalysis] = useState(false);

    const { canWrite: canExtractNow } = useVisibilityConfig();
    const canExtract = canExtractNow(VISIBILITY_KEYS.BANKING.EXTRACT_NOW);

    const pendingCount = useAppSelector(selectBankingPendingCount);
    const inFlightCount = useAppSelector(selectBankingInFlightCount);
    const extractedCount = useAppSelector(selectBankingExtractedCount);
    const triggering = useAppSelector(selectBankingTriggering);

    const orgId = params.id as string;
    const extractBankStatements = useExtractBankStatements(orgId);
    const { startRequest, isCurrent } = useLatestRequestGuard();
    // Separate monotonic guard for UNFILTERED fetches only (a subset of all fetches) — the
    // envelope capture below must survive being superseded by a later filtered fetch (that's
    // the whole point of the fix), so it can't share `isCurrent`/`requestToken` above. It only
    // needs to reject a genuinely out-of-order unfiltered response clobbering a newer one.
    const { startRequest: startUnfilteredRequest, isCurrent: isCurrentUnfiltered } = useLatestRequestGuard();

    // The org's bank-data envelope, derived once from the default (no-range) response; org-keyed so switching orgs drops the previous org's envelope.
    const [dataEnvelope, setDataEnvelope] = useState<{ orgId: string; envelope: BankDataEnvelope } | null>(null);
    // The cashflow view's transaction history reaches further back than the envelope's monthly summary, so union its span in to keep early months selectable on the CFB pill.
    const [cfbSpan, setCfbSpan] = useState<{ orgId: string; floor: string; ceiling: string } | null>(null);
    const bsEnvelope = dataEnvelope?.orgId === orgId ? dataEnvelope.envelope : null;
    const cfbEnvelope = cfbSpan?.orgId === orgId ? cfbSpan : null;
    const effectiveEnvelope =
        bsEnvelope || cfbEnvelope
            ? {
                  floor: [bsEnvelope?.floor, cfbEnvelope?.floor].filter(Boolean).sort()[0] as string,
                  ceiling: [bsEnvelope?.ceiling, cfbEnvelope?.ceiling].filter(Boolean).sort().slice(-1)[0] as string,
              }
            : null;
    const bankDateRange = useBankDateRange(orgId, effectiveEnvelope);
    // Applies the Cashflow default (Last 12 Months) once per org; a Set because BankDataTable isn't remounted on an org switch.
    const cfbDefaultAppliedOrgsRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        if (selectedBankingType !== CASHFLOW_FROM_BANKING || cfbDefaultAppliedOrgsRef.current.has(orgId)) return;
        cfbDefaultAppliedOrgsRef.current.add(orgId);
        if (!bankDateRange.rangeApplied) bankDateRange.selectLast12M();
    }, [selectedBankingType, orgId, bankDateRange.rangeApplied]);

    useEffect(() => {
        const el = cfbToolbarRef.current;
        if (!el) return;
        const read = () => setCollapseQuickRanges(el.clientWidth < CFB_INLINE_RANGES_MIN_WIDTH);
        read();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, [selectedBankingType]);

    useEffect(() => {
        const el = bankStatementsHeaderRef.current;
        if (!el) return;
        const read = () => {
            const threshold = selectedAccountId ? BANK_STATEMENTS_HEADER_MIN_WIDTH_WITH_ACCOUNT : BANK_STATEMENTS_HEADER_MIN_WIDTH;
            setCollapseExcelAnalysis(el.clientWidth < threshold);
        };
        read();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(read);
        ro.observe(el);
        return () => ro.disconnect();
    }, [selectedBankingType, selectedAccountId]);

    const cfbQuickRangeHandlers: Record<string, () => void> = {
        [QUICK_RANGE_KEYS.LAST_FY]: bankDateRange.selectLastFY,
        [QUICK_RANGE_KEYS.LAST_12M]: bankDateRange.selectLast12M,
        [QUICK_RANGE_KEYS.CURRENT_FY]: bankDateRange.selectCurrentFY,
    };

    const [cfbUnit, setCfbUnit] = useUnitPreference(CFB_UNIT_STORAGE_KEY);
    const cfbDivisor = AUDITED_UNIT_DIVISOR[cfbUnit] ?? 1;
    const [cfbDefaultWindow, setCfbDefaultWindow] = useState<{ orgId: string; start: string; end: string } | null>(null);
    const cfbWindow = cfbDefaultWindow?.orgId === orgId ? cfbDefaultWindow : null;

    const handleCfbDataSpan = (minDate: string, maxDate: string) => {
        const floor = (minDate || '').slice(0, 7);
        const ceiling = (maxDate || '').slice(0, 7);
        if (floor && ceiling) setCfbSpan({ orgId, floor, ceiling });
    };

    const handleCfbAppliedWindow = (startDate: string, endDate: string) => {
        const start = (startDate || '').slice(0, 7);
        const end = (endDate || '').slice(0, 7);
        if (start && end) setCfbDefaultWindow({ orgId, start, end });
    };

    const hasNoData = tableData?.isFetched && !tableData?.summaryData?.length;

    // Banking pending status is fetched once per org in Dashboard/index.tsx.
    // Do not re-dispatch here; Extract Now saga refetches on success + starts polling.

    // Org navigation doesn't remount this component — key the fetch on orgId so switching
    // orgs loads the new org's data instead of leaving the previous org's table on screen.
    // Also keyed on the applied Time Period range so picking/clearing a range refetches —
    // useBoundedClassificationPoll's refetchRef is reassigned every render (useBoundedPoll),
    // so the 30s background poll always carries the latest range via this same fetchData closure.
    useEffect(() => {
        fetchData();
    }, [orgId, bankDateRange.applied.startDate, bankDateRange.applied.endDate]);

    // G2 — refetch bank data when extraction transitions from in-flight to done.
    const prevInFlightRef = useRef<number>(inFlightCount);
    useEffect(() => {
        if (prevInFlightRef.current > 0 && inFlightCount === 0) {
            fetchData();
        }
        prevInFlightRef.current = inFlightCount;
    }, [inFlightCount]);

    const handleClassificationPollCapExhausted = () => {
        setTableData((prev) => ({ ...prev, classificationPending: false }));
    };

    const { isCapReached } = useBoundedClassificationPoll(
        orgId,
        tableData.classificationPending ?? false,
        () => fetchData(true),
        handleClassificationPollCapExhausted,
    );

    useEffect(() => {
        if (selectedAccount) {
            appendSeachParams(`${selectedAccount.id}`);
        }
    }, [selectedAccount]);

    const appendSeachParams = (selectedBankId: string) => {
        if (selectedBankId) updateQueryParams(searchParams, setSearchParams, { selectedBankId }, location);
    };

    const fetchData = (silent = false) => {
        if (!silent) setLoadingData(true);
        const requestToken = startRequest();
        // Only an UNFILTERED response should update the picker's min/max envelope, since filtered and unfiltered fetches can resolve in either order.
        const isUnfilteredFetch = !bankDateRange.rangeApplied;
        const unfilteredToken = isUnfilteredFetch ? startUnfilteredRequest() : null;
        const payload = bankDateRange.rangeApplied
            ? { startDate: bankDateRange.applied.startDate, endDate: bankDateRange.applied.endDate }
            : {};
        fetchBankStatementData(payload, {
            onSuccess: (data: any = {}) => {
                // Kept outside the `isCurrent(requestToken)` guard below — the envelope is org-data metadata, so a superseded (filtered) fetch's response still carries a valid one.
                if (unfilteredToken !== null && isCurrentUnfiltered(unfilteredToken) && data && !isEmpty(data)) {
                    const envelope = deriveBankDataEnvelope(data.summaryData, data.bankingOverview);
                    if (envelope) setDataEnvelope({ orgId, envelope });
                }

                if (!isCurrent(requestToken)) return;
                setLoadingData(false);

                // Handle completely empty API response
                if (!data || isEmpty(data)) {
                    setTableData({
                        isFetched: true,
                        accounts: undefined,
                        summaryData: [],
                        bankingOverview: [],
                    });
                    return;
                }

                setTableData({
                    isFetched: true,
                    accounts: data.accounts,
                    summaryData: data.summaryData || [],
                    bankingOverview: data.bankingOverview || [],
                    // A capped org-session (Pass-2 still pending at the 20-min budget) never
                    // trusts a late backend response back into "pending" — falls back to "-".
                    classificationPending: isCapReached() ? false : data.classificationPending === true,
                });

                const firstAccount = data?.summaryData?.[0];
                let selectedAccountFromParams = data?.summaryData?.find(
                    (account) => searchParams.get('selectedBankId') === (account?.accountNumber || account?.bankName),
                );
                if (selectedAccountFromParams) {
                    setSelectedAccount({
                        id: selectedAccountFromParams?.accountNumber || selectedAccountFromParams?.bankName,
                        label: `${selectedAccountFromParams?.bankName}${
                            selectedAccountFromParams?.accountNumber ? ': ' + selectedAccountFromParams?.accountNumber : ''
                        }`,
                    });
                } else if (firstAccount) {
                    setSelectedAccount({ id: 'Aggregated', label: 'Aggregated' });
                }
            },
            onError: () => {
                if (!isCurrent(requestToken)) return;
                setLoadingData(false);
                setTableData((tableData) => ({
                    ...tableData,
                    isFetched: true,
                }));
            },
        });
    };

    const dropdownOptions = useMemo(
        () =>
            tableData?.summaryData?.map((accountData) => ({
                id: accountData.accountNumber || accountData.bankName,
                label: `${accountData.bankName}${accountData.accountNumber ? ': ' + accountData.accountNumber : ''}`,
            })),
        [tableData?.summaryData],
    );

    const selectedTableData = useMemo((): any => {
        if (!selectedAccount?.id) return null;

        // For aggregated view
        if (selectedAccount.id.toString().toLowerCase() === AGGREGATED) {
            return tableData?.summaryData?.find((accountData) => accountData.bankName === 'Aggregated');
        }

        // For specific accounts, find directly in summaryData using the dropdown ID
        return tableData?.summaryData?.find(
            (accountData) =>
                (accountData.accountNumber && accountData.accountNumber === selectedAccount.id) ||
                (accountData.bankName && accountData.bankName === selectedAccount.id),
        );
    }, [selectedAccount?.id, tableData?.summaryData, loadingData]);

    const handleAccountSelectFromOverview = (accountId: string) => {
        if (accountId.toString().toLowerCase() === AGGREGATED) {
            setSelectedAccount({ id: 'Aggregated', label: 'Aggregated' });
            setSelectedAccountId('Aggregated');
            return;
        }

        // Get the overview account to find its name
        const bankingOverviewAccount = tableData?.bankingOverview?.find((acc) => acc.accountId === accountId);
        if (!bankingOverviewAccount) return;

        // Find matching account in summaryData using various matching strategies
        const matchingAccount = tableData?.summaryData?.find((accountData) => {
            return bankingOverviewAccount.accountNumber === accountData.accountNumber;
        });

        if (matchingAccount) {
            const selectedOption = {
                id: matchingAccount.accountNumber || matchingAccount.bankName,
                label: `${matchingAccount.bankName}${matchingAccount.accountNumber ? ': ' + matchingAccount.accountNumber : ''}`,
            };
            setSelectedAccount(selectedOption);
            setSelectedAccountId(selectedOption.id);
        }
    };

    const handleAccountSelectFromDropdown = (selectedOption: DropdownOption) => {
        setSelectedAccount(selectedOption);
        setSelectedAccountId(selectedOption.id as string);
    };

    const handleExtractClick = (clickSource: ClickSource) => {
        // AICA_BANK_EXTRACT_NOW_CLICKED now fires centrally in useExtractBankStatements
        // so every call site (this one + the banner in Dashboard/index.tsx) emits once.
        extractBankStatements(clickSource);
    };

    const handleViewAnalysis = () => {
        const orgId = params.id as string;
        if (!orgId) {
            console.error('Organization ID not found');
            return;
        }
        const reportWindow = bankDateRange.rangeApplied
            ? { startMonth: bankDateRange.startMonth, endMonth: bankDateRange.endMonth }
            : undefined;
        triggerBsaReport(orgId, reportWindow);
    };

    const noData = useMemo(() => {
        if (tableData.summaryData?.length === 0) return true;
        // Check if headers are empty or missing
        const hasNoHeaders = !selectedTableData?.headers?.length;
        // Check if rows are empty or missing
        const hasNoRows = !selectedTableData?.rows?.length;
        return hasNoHeaders || hasNoRows;
    }, [tableData.summaryData]);

    // Passed to BankingOverview so its "(LTM)" header/tooltip strings are only shown when no
    // range is applied (they'd otherwise be untrue) — BankingOverview owns joining these into
    // the tooltip sentence.
    const startMonthDisplay = bankDateRange.rangeApplied ? monthKeyToDisplay(bankDateRange.startMonth) : undefined;
    const endMonthDisplay = bankDateRange.rangeApplied ? monthKeyToDisplay(bankDateRange.endMonth) : undefined;

    const getEmptyBankText = () => {
        if (hasNoData) return 'No data available';
        if (!selectedTableData) return 'Please select the bank account to view';
        if (selectedTableData && (!selectedTableData.headers?.length || !selectedTableData.rows?.length)) {
            return 'Data Not Available';
        }

        return 'Please select the bank account to view';
    };

    const renderBankStatementsContent = () => {
        // MUST be the first statement here: a cold load already has noData=true (no
        // headers yet), so a spinner check placed after the noData/hasNoData branch
        // below would render the EmptyBank "Please select the bank account to view" text
        // instead of a spinner on every first load. Scoped to renderBankStatementsContent
        // (not the whole component) so .Top — the Time Period chips, Excel Analysis pill,
        // and breadcrumb — stays mounted across every applied-range fetch, preserving
        // MonthYearPicker's internal isOpen/viewYear state.
        if (loadingData) {
            return <LoadingSpinner height="60px" style={{ marginTop: '100px' }} color="black" />;
        }

        if (noData || hasNoData) {
            const hasInFlight = triggering || inFlightCount > 0;
            if (hasInFlight) {
                return (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '60vh',
                            gap: '12px',
                        }}
                    >
                        <LoadingSpinner color="#6021b3" height="48px" style={{ marginTop: 0 }} />
                        <div style={{ fontFamily: 'Gilroy-Medium,sans-serif', fontSize: 14, color: '#4d4d4d' }}>
                            Extraction in progress…
                        </div>
                    </div>
                );
            }
            const isExtractableEmpty = extractedCount === 0 && pendingCount > 0;
            if (isExtractableEmpty) {
                return <EmptyBank showExtractCta={canExtract} onExtractClick={() => handleExtractClick(CLICK_SOURCE.EMPTY_STATE)} />;
            }
            return <EmptyBank text={getEmptyBankText()} />;
        }

        if (!selectedAccountId) {
            return (
                <BankingOverview
                    data={(tableData.bankingOverview || []) as any}
                    summaryData={(tableData.summaryData || []) as any}
                    onAccountSelect={handleAccountSelectFromOverview}
                    startMonthDisplay={startMonthDisplay}
                    endMonthDisplay={endMonthDisplay}
                />
            );
        }

        return (
            <div className={styles.TableContainer}>
                <table className={styles.BankTable}>
                    <thead className={styles.TableHead}>
                        <Header data={selectedTableData?.headers} />
                    </thead>
                    <tbody className={styles.TableBody}>
                        <Rows
                            rows={selectedTableData?.rows}
                            headers={selectedTableData?.headers}
                            tableRef={tableRef}
                            classificationPending={tableData.classificationPending}
                        />
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <div className={styles.BankingModule}>
            <div className={styles.BankingSelector}>
                {BANKING_TYPES.map((type, i) => (
                    <button
                        type="button"
                        key={`${type.id}-${i}`}
                        aria-pressed={type.id === selectedBankingType}
                        className={clsx(styles.BankingOption, {
                            [styles.BankingSelectedOption]: type.id === selectedBankingType,
                        })}
                        onClick={() => {
                            setSelectedBankingType(type.id);
                            updateQueryParams(searchParams, setSearchParams, { bankingType: type.id }, location);
                        }}
                    >
                        {type.label}
                    </button>
                ))}
            </div>
            {/* Second header band, directly under the pill row: Units (left) · Source + Time
                Period (right). Mirrors the Financials module's sticky sub-header. Kept a
                sibling of the pill row, not a child of .BankTableContainer, for the same
                reason: that container is overflow-x auto and would capture a sticky child. */}
            {selectedBankingType === CASHFLOW_FROM_BANKING && (
                <div className={styles.CfbToolbar} ref={cfbToolbarRef}>
                    <div className={styles.CfbLeft}>
                        <span className={styles.CfbUnitLabel}>Units ₹:</span>
                        <EditableDropdown
                            id="UnitsSelector"
                            value={cfbUnit}
                            options={AUDITED_UNIT_OPTIONS}
                            setOutput={(val: string) => setCfbUnit(val)}
                            removeInput={true}
                            optionStyle={{ fontSize: '0.875rem' }}
                            placeHolder={AUDITED_UNIT_DEFAULT}
                            wrapperStyle={{ width: '6.5rem' }}
                        />
                    </div>
                    <div className={styles.CfbRight}>
                        <span data-testid="cfb-source-chip" className={styles.CfbSourceChip}>
                            Source: <span className={styles.CfbSourceValue}>Bank Statements</span>
                        </span>
                        <TimePeriodFilter
                            startMonth={bankDateRange.startMonth}
                            endMonth={bankDateRange.endMonth}
                            startPickerMin={bankDateRange.startPickerMin}
                            startPickerMax={bankDateRange.startPickerMax}
                            endPickerMin={bankDateRange.endPickerMin}
                            endPickerMax={bankDateRange.endPickerMax}
                            rangeApplied={bankDateRange.rangeApplied}
                            onSelectStartMonth={bankDateRange.selectStartMonth}
                            onSelectEndMonth={bankDateRange.selectEndMonth}
                            onClear={bankDateRange.clear}
                            defaultStartMonth={cfbWindow?.start}
                            defaultEndMonth={cfbWindow?.end}
                        />
                        {/* Quick ranges: shown inline while the row has room, and folded into the
                            overflow menu once it doesn't — the Comments panel takes roughly a
                            quarter of the width away. Same options and the same handlers either
                            way; only the presentation changes. The active one is DERIVED from the
                            chosen months (see the hook), so picking the same span by hand in the
                            pickers still shows as selected in both forms. */}
                        {collapseQuickRanges ? (
                            // ThreeDotMenu only honours `className` as a 'Delete' sentinel, so the
                            // selected state rides inside `name`, which is typed as a ReactNode.
                            <ThreeDotMenu
                                tableName="CfbQuickRanges"
                                menuItems={CFB_QUICK_RANGES.map(({ id, label }) => ({
                                    name: (
                                        <span
                                            className={clsx(styles.RangeMenuItem, {
                                                [styles.RangeMenuItemActive]: bankDateRange.activeQuickRange === id,
                                            })}
                                        >
                                            {label}
                                        </span>
                                    ),
                                    onClick: () => cfbQuickRangeHandlers[id]?.(),
                                }))}
                            />
                        ) : (
                            <TypeSelector
                                testId="cfb-range-toggle"
                                options={CFB_QUICK_RANGES}
                                selectedOptionId={bankDateRange.activeQuickRange}
                                changeSelectedOption={(id: string) => cfbQuickRangeHandlers[id]?.()}
                            />
                        )}
                    </div>
                </div>
            )}
            {selectedBankingType === BANK_STATEMENTS && (
                <div className={styles.Top} ref={bankStatementsHeaderRef}>
                    <div className={styles.Left}>
                        <h2 className={styles.HeaderTitle}>Banking Overview</h2>
                        {selectedAccountId && (
                            <Dropdown
                                optionsList={dropdownOptions || []}
                                selectedOption={selectedAccount}
                                hideClearIcon={true}
                                itemClickHandler={(selectedOption) => {
                                    setLoadingData(true);
                                    handleAccountSelectFromDropdown(selectedOption);
                                    setTimeout(() => {
                                        setLoadingData(false);
                                    }, 300);
                                }}
                                style={{
                                    width: '18.75rem',
                                    fontFamily: 'var(--text-gilroy-semibold)',
                                    borderRadius: '0.5rem',
                                }}
                                inputStyle={{ fontFamily: 'var(--text-gilroy-semibold)' }}
                                placeholder={'Select Account'}
                                variant="white"
                                size="tiny"
                            />
                        )}
                    </div>

                    <div className={styles.Right}>
                        {collapseExcelAnalysis ? (
                            <ThreeDotMenu
                                tableName="BankStatementsHeaderActions"
                                menuItems={[
                                    {
                                        name: bsaReportLoading ? EXCEL_ANALYSIS_LOADING_LABEL : EXCEL_ANALYSIS_LABEL,
                                        onClick: handleViewAnalysis,
                                        disabled: bsaReportLoading,
                                    },
                                ]}
                            />
                        ) : (
                            <CtaPill
                                tone="blue"
                                icon={changeCircleBlue}
                                label={EXCEL_ANALYSIS_LABEL}
                                loadingLabel={EXCEL_ANALYSIS_LOADING_LABEL}
                                loading={bsaReportLoading}
                                onClick={handleViewAnalysis}
                            />
                        )}
                        <TimePeriodFilter
                            startMonth={bankDateRange.startMonth}
                            endMonth={bankDateRange.endMonth}
                            startPickerMin={bankDateRange.startPickerMin}
                            startPickerMax={bankDateRange.startPickerMax}
                            endPickerMin={bankDateRange.endPickerMin}
                            endPickerMax={bankDateRange.endPickerMax}
                            rangeApplied={bankDateRange.rangeApplied}
                            onSelectStartMonth={bankDateRange.selectStartMonth}
                            onSelectEndMonth={bankDateRange.selectEndMonth}
                            onClear={bankDateRange.clear}
                        />
                    </div>
                </div>
            )}
            <div className={styles.BankingContent}>
                <div className={styles.BankTableContainer} ref={tableRef}>
                    {/* Banner renders at Dashboard level — see modules/Dashboard/index.tsx */}
                    {selectedBankingType === CASHFLOW_FROM_BANKING ? (
                        <>
                            <FinancialsFromBankingV2
                                startDate={bankDateRange.applied.startDate}
                                endDate={bankDateRange.applied.endDate}
                                onDataSpan={handleCfbDataSpan}
                                onAppliedWindow={handleCfbAppliedWindow}
                                unitDivisor={cfbDivisor}
                            />
                        </>
                    ) : (
                        <>
                            <BankStatementsBreadcrumb
                                selectedAccountId={selectedAccountId}
                                accountLabel={selectedAccount?.label}
                                onBack={() => setSelectedAccountId(null)}
                            />
                            <div className={styles.Content}>{renderBankStatementsContent()}</div>
                            {showBsaFile && (
                                <FileViewerDrawer
                                    heading={bsaFileName || BSA_REPORT_VIEWER_HEADING}
                                    fileUrl={bsaFileUrl}
                                    mimeType={bsaFileMimeType}
                                    onDownload={downloadBsaFile}
                                    onClose={closeBsaFile}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default BankDataTable;
